import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { BarChart3, TrendingUp, AlertTriangle, Loader2, RefreshCw, Lightbulb } from "lucide-react";
import { isLocked } from "@/utils/subscription";
import { LockOverlay } from "@/components/LockBadge";

export default function InsightsPage() {
  const { api, user } = useAuth();
  const { t, lang } = useLanguage();
  const locked = isLocked(user);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchInsights = async () => {
    if (isLocked(user)) { setLoading(false); return; }  // не-підписник: не викликаємо гейтнутий /ai/smart-insights (уникаємо 402)
    setLoading(true);
    try {
      const res = await api().get("/ai/smart-insights");
      setData(res.data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const insightIcons = {
    nutrition: <TrendingUp className="w-5 h-5 text-[#5C7A5E]" strokeWidth={1.5} />,
    variety: <BarChart3 className="w-5 h-5 text-[#7B9E7F]" strokeWidth={1.5} />,
    waste: <AlertTriangle className="w-5 h-5 text-[#BC4749]" strokeWidth={1.5} />,
    budget: <Lightbulb className="w-5 h-5 text-[#E9C46A]" strokeWidth={1.5} />,
    health: <TrendingUp className="w-5 h-5 text-[#D4856B]" strokeWidth={1.5} />,
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] pb-28" data-testid="insights-page">
      <div className="pl-[72px] pr-5 pt-6 pb-4">
        <h1 className="font-heading text-3xl text-[#3D4035]">{t("insightsTitle")}</h1>
      </div>

      {locked ? (
        <div className="px-5">
          <LockOverlay>
            <div className="space-y-4">
              {[0, 1, 2].map((i) => (
                <div key={i} className="bg-white rounded-2xl p-5 border border-black/5">
                  <div className="h-4 w-1/3 bg-[#E8E5E0] rounded-full mb-3" />
                  <div className="h-3 w-full bg-[#EBE9E4] rounded-full mb-1.5" />
                  <div className="h-3 w-4/5 bg-[#EBE9E4] rounded-full" />
                </div>
              ))}
            </div>
          </LockOverlay>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 text-[#7B9E7F] animate-spin" />
        </div>
      ) : (
        <div className="px-5 space-y-6">
          {/* Weekly Summary */}
          {data?.weekly_summary && (
            <div data-testid="weekly-summary-card" className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-black/5">
              <h2 className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold mb-4">{t("weeklySummary")}</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#D4856B]/10 rounded-2xl p-4 text-center">
                  <p className="font-heading text-2xl text-[#3D4035]">{data.weekly_summary.avg_calories || "—"}</p>
                  <p className="font-body text-xs text-[#A5A58D] uppercase font-bold">{t("avgCalories")}</p>
                </div>
                <div className="bg-[#5C7A5E]/10 rounded-2xl p-4 text-center">
                  <p className="font-heading text-2xl text-[#3D4035]">{data.weekly_summary.variety_score || "—"}/10</p>
                  <p className="font-body text-xs text-[#A5A58D] uppercase font-bold">{t("varietyScore")}</p>
                </div>
              </div>

              {data.weekly_summary.top_ingredients?.length > 0 && (
                <div className="mt-4">
                  <p className="font-body text-xs text-[#6B705C] uppercase tracking-widest font-bold mb-2">{t("topIngredients")}</p>
                  <div className="flex flex-wrap gap-2">
                    {data.weekly_summary.top_ingredients.map((ing, idx) => (
                      <span key={idx} className="px-3 py-1 bg-[#F2E9E4] rounded-full font-body text-sm text-[#3D4035]">{ing}</span>
                    ))}
                  </div>
                </div>
              )}

              {data.weekly_summary.waste_risk_items?.length > 0 && (
                <div className="mt-4">
                  <p className="font-body text-xs text-[#BC4749] uppercase tracking-widest font-bold mb-2">{t("wasteRisk")}</p>
                  <div className="flex flex-wrap gap-2">
                    {data.weekly_summary.waste_risk_items.map((item, idx) => (
                      <span key={idx} className="px-3 py-1 bg-[#BC4749]/10 rounded-full font-body text-sm text-[#BC4749]">{item}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Empty state */}
          {!data?.weekly_summary && !data?.insights?.length && (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-[#7B9E7F]/10 flex items-center justify-center mx-auto mb-4">
                <BarChart3 className="w-8 h-8 text-[#7B9E7F]" strokeWidth={1.5} />
              </div>
              <p className="font-body font-semibold text-[#3D4035] mb-2">{t("noInsightsYet") || "Поки немає інсайтів"}</p>
              <p className="font-body text-sm text-[#A5A58D]">{t("noInsightsDesc") || "Готуйте більше страв — AI аналізує ваш раціон і надасть персональні поради"}</p>
            </div>
          )}

          {/* Insights Cards */}
          {data?.insights?.map((insight, idx) => (
            <div
              key={idx}
              data-testid={`insight-card-${idx}`}
              className="bg-white rounded-2xl p-5 shadow-sm border border-black/5"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#F2E9E4] flex items-center justify-center shrink-0">
                  {insightIcons[insight.type] || insightIcons.health}
                </div>
                <div>
                  <p className="font-body font-semibold text-[#3D4035] mb-1">{insight.title}</p>
                  <p className="font-body text-sm text-[#6B705C] leading-relaxed">{insight.message}</p>
                  {insight.suggestion && (
                    <p className="font-body text-sm text-[#7B9E7F] mt-2 font-medium">{insight.suggestion}</p>
                  )}
                </div>
              </div>
            </div>
          ))}

          <button
            data-testid="refresh-insights-button"
            onClick={fetchInsights}
            disabled={loading}
            className="w-full bg-[#F2E9E4] text-[#7B9E7F] rounded-full h-12 font-body font-bold hover:bg-[#E8E5E0] transition-all flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
            {t("refreshInsights")}
          </button>
        </div>
      )}
    </div>
  );
}
